import React, { useState } from "react";
import {
  Button,
  Empty,
  Tag,
  Divider,
  Row,
  Col,
  Typography,
  Table,
  Icon,
  Spin,
  Tooltip,
} from "../../common/UIComponents";
import { splitOrders } from "../../api/OrdersApi";
import { formatDimension_v2, formatWeight } from "../../helpers/orders";
import { convertToKilograms, renderAlertMessage } from "../../helpers/common";
import Button_v2 from "../../stories/components/Button_v2";

const { Text } = Typography;

const SplitOrderContainer = props => {
  const { onCancel, currentOrder = {}, onSplitSuccess } = props;
  const { order_items: orderItems = [] } = currentOrder || {};

  const [selectedItems, setSelectedItems] = useState({
    selectedItemKeys: [],
    selectedItemRows: [],
  });
  const [splitOrderDetails, setSplitOrderDetails] = useState([]);
  const [loading, setLoading] = useState(false);

  const rowSelection = {
    selectedRowKeys: selectedItems.selectedItemKeys,
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedItems({
        selectedItemKeys: selectedRowKeys,
        selectedItemRows: selectedRows,
      });
    },
    getCheckboxProps: record => ({
      disabled: record.is_split_item === true,
    }),
  };

  const columns = [
    {
      title: "Item Name",
      dataIndex: "item_name",
      key: "item_name",
      render: text => <Text strong>{text}</Text>,
      width: 200,
      fixed: "left",
    },
    {
      title: "Model No.",
      dataIndex: "item_model",
      key: "item_model",
      width: 110,
    },
    {
      title: "Serial No.",
      dataIndex: "serial_number",
      key: "serial_number",
      render: text => <Text>{text}</Text>,
      width: 130,
    },
    {
      title: "Pcs",
      dataIndex: "item_quantity",
      key: "item_quantity",
      align: "center",
      width: 60,
    },
    {
      title: "Wt.",
      dataIndex: "item_weight",
      key: "item_weight",
      render: (_, record) => (
        <div>
          <span>{formatWeight(record.item_weight, true, record.item_weight, "lbs")}</span>
          <br />
          <span style={{ color: "#d0d0d0" }}>
            {formatWeight(record.item_weight_in_kgs, false, "", "kgs")}
          </span>
        </div>
      ),
      width: 100,
    },
    {
      title: "Wt. Type",
      dataIndex: "weight_type",
      key: "weight_type",
      width: 100,
      align: "center",
      render: (_, record) => (record.is_indv_weight === 1 ? "Individual" : "Complete"),
    },
    {
      title: "Total Wt.",
      dataIndex: "total_weight",
      key: "total_weight",
      width: 110,
      align: "right",
      sorter: (a, b) => a.total_weight - b.total_weight,
      render: (text, record) => (
        <div style={{ textAlign: "right" }}>
          <span>{record?.total_weight || 1} lbs</span>
          <br />
          <span style={{ color: "#d0d0d0" }}>
            {convertToKilograms(record?.total_weight || 1, true)}
          </span>
        </div>
      ),
    },
    {
      title: "Dimensions (L×W×H)",
      key: "dimensions",
      align: "center",
      render: (_, record) => (
        <div style={{ textAlign: "center" }}>
          {formatDimension_v2(
            record.item_length,
            record.item_length_uom,
            record.item_width,
            record.item_width_uom,
            record.item_height,
            record.item_height_uom
          )}
        </div>
      ),
      width: 160,
    },
  ];

  const handleCreate = async () => {
    setLoading(true);
    try {
      const payload = {
        item_ids: selectedItems.selectedItemKeys || [],
        order_ids: [currentOrder.id],
        appointment_type: "EXISTING",
        is_split_order: true,
      };
      const response = await splitOrders(payload);
      if (response.success) {
        setSplitOrderDetails(response.orders);
        if (onSplitSuccess) {
          onSplitSuccess(response.orders);
        }
      } else {
        renderAlertMessage(response?.errors ? response.errors : ["Failed to Split Order"]);
      }
    } catch (error) {
      console.error("Split order API error:", error);
    } finally {
      setLoading(false);
    }
  };

  const hasItems = orderItems && orderItems.length > 0;
  const hasSelection = selectedItems.selectedItemKeys.length > 0;

  return (
    <div style={{ padding: "8px 0" }}>
      <Spin spinning={loading} tip="Creating split order...">
        {/* Summary bar */}
        {hasItems && (
          <div
            style={{
              background: "#fafafa",
              border: "1px solid #e8e8e8",
              borderRadius: 4,
              padding: "8px 12px",
              marginBottom: 16,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text type="secondary">
              {orderItems.length} item{orderItems.length !== 1 ? "s" : ""} available
            </Text>
            {orderItems.some(item => item.is_split_item === true) && (
              <Tooltip
                title={`${orderItems.filter(i => i.is_split_item).length} already splitted item${
                  orderItems.filter(i => i.is_split_item).length > 1 ? "s" : ""
                } — cannot be selected`}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    fontSize: 12,
                    color: "#534AB7",
                    background: "#eeeffe",
                    border: "0.5px solid #afa9ec",
                    borderRadius: 4,
                    padding: "2px 8px",
                    cursor: "default",
                  }}
                >
                  <Icon type="apartment" style={{ fontSize: 13, color: "#534AB7" }} />
                  {orderItems.filter(i => i.is_split_item).length} splitted
                </span>
              </Tooltip>
            )}
            {hasSelection && (
              <Text style={{ color: "#1890ff" }}>
                {selectedItems.selectedItemKeys.length} selected
              </Text>
            )}
          </div>
        )}

        {/* Table or Empty state */}
        {hasItems ? (
          <div style={{ overflowX: "auto" }}>
            <Table
              // className="fixed-header-table"
              rowSelection={rowSelection}
              columns={columns}
              dataSource={orderItems}
              rowKey="id"
              size="small"
              pagination={false}
              scroll={{ x: "max-content", y: "calc(100vh - 480px)" }}
              bordered={false}
              style={{ marginBottom: 16 }}
              rowClassName={record => (record.is_split_item === true ? "split-item-row" : "")}
            />
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "16px",
              border: "1px dashed #d9d9d9",
              borderRadius: 6,
              background: "#fafafa",
              marginBottom: 16,
              textAlign: "center",
            }}
          >
            <Icon type="inbox" style={{ fontSize: 24, color: "#bfbfbf", marginBottom: 6 }} />
            <div>
              <Text type="secondary" style={{ fontSize: 13, display: "block" }}>
                No items found in this order
              </Text>
              <Text style={{ fontSize: 12, color: "#bfbfbf" }}>
                Use the buttons below to create a split order or cancel.
              </Text>
            </div>
          </div>
        )}

        <Divider style={{ margin: "8px 0 16px" }} />

        {/* Action buttons */}
        <Row type="flex" justify="end" gutter={8}>
          <Col>
            <Button_v2
              className="small-btn flat"
              text="Cancel"
              disabled={loading}
              onClick={onCancel}
            />
          </Col>

          <Col>
            <Button_v2
              className="small-btn raised"
              text="Create Split Order"
              loading={loading}
              onClick={handleCreate}
            />
          </Col>
        </Row>
      </Spin>
    </div>
  );
};

export default SplitOrderContainer;
